import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, dateiLaden, fehlertext } from "./api";
import { bereichsName, useBereiche } from "./bereiche";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Seitenkopf } from "./ui";
import { sprache } from "./datum";

/**
 * Das Team: Zugaenge, Wochenstunden, Feiertage.
 *
 * Drei Dinge auf einer Seite, weil sie zusammen gebraucht werden. Wer einen
 * Mitarbeiter anlegt, hinterlegt im selben Zug seine Wochenstunden, sonst
 * steht er spaeter ohne Soll da und die Ueberstunden bleiben leer. Und ohne
 * Feiertagsliste rechnet das Soll an Weihnachten weiter.
 *
 * Ein Zugang entsteht ueber eine Einladung, nicht ueber ein Passwort, das
 * jemand vergibt und weitersagt. Der Mitarbeiter setzt sein Passwort selbst,
 * und niemand sonst kennt es.
 */

interface Person {
  id: string;
  display_name: string;
  email: string;
  role: string;
  locale: string;
  phone: string | null;
  weekly_hours: string | null;
  employment_start: string | null;
  employment_end: string | null;
  is_active: boolean;
  /** Welche Abteilungen dieser Mensch macht: Garten, Pool, Reinigung. */
  abteilungen: { id: string; name: string; aushilfe: boolean }[];
  /** Wer ihn mitnimmt. Leer heisst: er faehrt selbst. */
  faehrtMitId: string | null;
  faehrtMit: string | null;
  wohnort: string | null;
  wohnortGefunden: boolean;
  /** Platz der Spalte auf dem Dienstplan, klein zuerst. Leer heisst hinten. */
  planPosition: number | null;
  /** Ob er eine eigene Spalte auf dem Dienstplan bekommt. */
  imDienstplan: boolean;
}

interface Abteilung {
  id: string;
  name: string;
}

/** Die Abteilung samt Belegung. So kommt sie aus der Liste der Einstellungen. */
interface AbteilungVoll extends Abteilung {
  aktiv: boolean;
  mitarbeiter: number;
  objekte: number;
  /**
   * Welche Bereiche der Checkliste zu dieser Abteilung gehoeren.
   *
   * Steuert, welche Pruefpunkte ein Mitarbeiter im Bericht des Tages sieht.
   * Mehrere sind der Regelfall. Leer heisst keine Einschraenkung, seine Leute
   * sehen den ganzen Bericht. Die Bereiche selbst kommen aus dem Baukasten,
   * seit sie dort gepflegt werden; eine feste Liste stand hier bis zum
   * 03.09.2026 und war der Zuschnitt eines einzigen Betriebs.
   */
  checklistBereiche: string[];
}

interface Anwesenheit {
  benutzerId: string;
  zustand: "arbeitet" | "pause" | "vergessen" | "abwesend" | "nicht_da";
  seit: string | null;
  grund: string | null;
}

const ROLLEN = ["employee", "lead", "admin"] as const;
const SPRACHEN = ["de", "en", "es", "ar"] as const;

/** Wie oft die Anwesenheit von selbst nachgeladen wird. */
const TAKT_MS = 60_000;

export function TeamSeite() {
  const { t } = useTranslation();
  const [personen, setPersonen] = useState<Person[] | null>(null);
  const [anwesend, setAnwesend] = useState<Record<string, Anwesenheit>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const [einladen, setEinladen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<Person | null>(null);
  const [abteilungen, setAbteilungen] = useState<AbteilungVoll[]>([]);

  const laden = useCallback(async () => {
    const a = await api.get<{ users: Person[] }>("/auth/users?alle=ja");
    setPersonen(a.users);
  }, []);

  useEffect(() => {
    // Mit den stillgelegten: die Geschaeftsleitung soll sehen, was es einmal
    // gab, und eine Abteilung auch wieder in Betrieb nehmen koennen.
    void api.get<{ abteilungen: AbteilungVoll[] }>("/abteilungen?alle=ja")
      .then((a) => setAbteilungen(a.abteilungen)).catch(() => setAbteilungen([]));
  }, []);

  /**
   * Die Anwesenheit kommt aus einem eigenen Aufruf, aus zwei Gruenden.
   *
   * Sie veraltet im Minutentakt, die Stammdaten daneben nicht. Und sie ist
   * die einzige Angabe auf dieser Seite, die ein Mitarbeiter nicht sehen darf:
   * wer krank ist, geht Kollegen nichts an. Deshalb liegt sie hinter einem
   * eigenen, nur der Geschaeftsleitung offenen Weg.
   *
   * Ein Fehler beim Nachladen bleibt still. Dass gerade keine Punkte
   * dastehen, ist besser als eine rote Meldung ueber der Mitarbeiterliste,
   * die mit den Stammdaten nichts zu tun hat.
   */
  const anwesenheitLaden = useCallback(async () => {
    try {
      const a = await api.get<{ anwesenheit: Anwesenheit[] }>("/anwesenheit");
      setAnwesend(Object.fromEntries(a.anwesenheit.map((x) => [x.benutzerId, x])));
    } catch {
      /* still */
    }
  }, []);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  useEffect(() => {
    void anwesenheitLaden();
    const uhr = window.setInterval(() => void anwesenheitLaden(), TAKT_MS);
    return () => window.clearInterval(uhr);
  }, [anwesenheitLaden]);

  return (
    <>
      <Seitenkopf
        titel={t("team.titel")}
        hinweis={t("team.hinweis")}
        aktionen={<Knopf onClick={() => setEinladen(true)}>{t("team.einladen")}</Knopf>}
      />
      <Fehler text={fehler} />

      {!personen ? <Laedt /> : (
        <ul className="space-y-2">
          {personen.map((p) => (
            <li key={p.id}>
              <Karte className={`p-4 ${p.is_active ? "" : "opacity-60"}`}>
                {/*
                  Feste Zeilen statt eines umbrechenden Bandes.

                  Vorher standen Punkt, Name, Rolle, Mailadresse und Knopf in
                  einer einzigen Zeile mit Umbruch. Am Telefon brach jede Karte
                  an einer anderen Stelle um: bei dem einen stand die Rolle
                  hinter dem Namen, beim naechsten darunter, beim dritten
                  sprang der Knopf in die Mitte. Vier Karten sahen aus wie vier
                  verschiedene Masken.

                  Jetzt immer dieselben drei Zeilen: Name mit Zustand, dann
                  Rolle und Mailadresse, dann der Vertrag. Der Knopf steht am
                  Bildschirm oben rechts neben dem Namen und am Telefon am
                  Ende der Vertragszeile.
                */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold [overflow-wrap:anywhere]">{p.display_name}</p>
                    {/* Der Zustand direkt unter dem Namen: die Frage "wer ist
                        heute da" beantwortet sich beim Ueberfliegen. */}
                    <Zustandsmarke stand={anwesend[p.id]} />
                  </div>
                  <div className="hidden shrink-0 sm:block">
                    <Knopf art="neben" onClick={() => setBearbeitet(p)}>
                      {t("aktion.bearbeiten")}
                    </Knopf>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">
                    {t(`team.rolle.${p.role}`)}
                  </span>
                  {!p.is_active && (
                    <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                      {t("team.stillgelegt")}
                    </span>
                  )}
                  <span className="text-sm text-leise [overflow-wrap:anywhere]">{p.email}</span>
                </div>

                {/* Wer welche Abteilung macht. Ohne diese Zeile ist beim
                    Planen jede Zuteilung Raten. */}
                {p.abteilungen.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {p.abteilungen.map((a) => (
                      <span
                        key={a.id}
                        className={`rounded px-2 py-0.5 text-xs font-semibold ${
                          a.aushilfe
                            ? "border border-marke/40 text-leise"
                            : "bg-marke/15 text-verweis"
                        }`}
                      >
                        {a.aushilfe ? t("team.abteilungAushilfe", { abteilung: a.name }) : a.name}
                      </span>
                    ))}
                  </div>
                )}

                {/*
                  Am Telefon steht der Knopf am Ende der Vertragszeile und
                  nicht darunter. Ueber die volle Breite kostete er je Karte
                  eine eigene Zeile, und bei vier Mitarbeitern war das ein
                  Viertel Bildschirm fuer viermal dasselbe Wort. Neben dem
                  Text ist der Platz ohnehin frei.
                */}
                <div className="mt-1 flex items-center justify-between gap-3">
                  <p className="text-sm text-leise">
                    {p.weekly_hours
                      ? t("team.wochenstundenWert", { stunden: Number(p.weekly_hours) })
                      : t("team.ohneWochenstunden")}
                    {p.employment_start ? ` · ${t("team.seit", { datum: kurz(p.employment_start) })}` : ""}
                    {p.employment_end ? ` · ${t("team.bis", { datum: kurz(p.employment_end) })}` : ""}
                    {/* Wer kein eigenes Auto hat, gehoert auf die Karte und
                        nicht nur in den Dialog: es entscheidet mit, wie die
                        Woche aussieht. */}
                    {p.faehrtMit ? ` · ${t("team.faehrtMitWert", { name: p.faehrtMit })}` : ""}
                  </p>
                  <div className="shrink-0 sm:hidden">
                    <Knopf art="neben" onClick={() => setBearbeitet(p)}>
                      {t("aktion.bearbeiten")}
                    </Knopf>
                  </div>
                </div>
              </Karte>
            </li>
          ))}
        </ul>
      )}

      <Abteilungsliste abteilungen={abteilungen} onGeaendert={() => {
        void api.get<{ abteilungen: AbteilungVoll[] }>("/abteilungen?alle=ja")
          .then((a) => setAbteilungen(a.abteilungen)).catch(() => undefined);
        void laden();
      }} />

      <Feiertage />

      {einladen && (
        <EinladungDialog onClose={() => setEinladen(false)} onFertig={() => { setEinladen(false); void laden(); }} />
      )}
      {bearbeitet && (
        <PersonDialog
          person={bearbeitet}
          /*
           * Als Fahrer kommt nur infrage, wer selbst faehrt und nicht der
           * Mensch selbst. Beides schon in der Auswahl, damit die Absage des
           * Servers gar nicht erst noetig wird.
           */
          fahrer={(personen ?? []).filter((p) => p.id !== bearbeitet.id && !p.faehrtMitId)}
          // Nur die aktiven: in eine stillgelegte Abteilung teilt niemand
          // mehr jemanden ein.
          abteilungen={abteilungen.filter((a) => a.aktiv)}
          onClose={() => setBearbeitet(null)}
          onFertig={() => { setBearbeitet(null); void laden(); }}
        />
      )}
    </>
  );
}

/**
 * Der farbige Punkt vor dem Namen.
 *
 * Vier Farben und ein leerer Ring, mehr nicht. Wer acht Farben unterscheiden
 * muss, liest am Ende doch wieder die Zeile.
 *
 *   gruen        gestempelt, arbeitet gerade
 *   rot          krank
 *   blau         Urlaub
 *   Bernstein    Ausstempeln vergessen, hier muss jemand ran
 *   leerer Ring  nicht da, ohne dass etwas offen waere
 *
 * Der Punkt steht nie allein. Neben ihm steht dasselbe in Worten, und zwar
 * aus zwei Gruenden: rotgruenblind ist etwa jeder zwoelfte Mann, und im
 * Ausdruck einer Liste ist jede Farbe grau. Die Farbe ist die Abkuerzung fuer
 * den schnellen Blick, das Wort ist die Auskunft.
 *
 * Schulung, Sonderurlaub und unbezahlt frei bekommen bewusst keine eigene
 * Farbe. Sie sind angekuendigte Abwesenheiten, bei denen niemand etwas tun
 * muss; sie stehen als Wort neben dem leeren Ring.
 */
function Zustandsmarke({ stand }: { stand: Anwesenheit | undefined }) {
  const { t, i18n } = useTranslation();
  // Ohne Angabe kein Punkt. Das betrifft stillgelegte Zugaenge: dort waere
  // "nicht eingestempelt" eine Feststellung ohne Aussage.
  if (!stand) return null;

  const krank = stand.zustand === "abwesend" && stand.grund === "sick";
  const urlaub = stand.zustand === "abwesend" && stand.grund === "vacation";
  const arbeitet = stand.zustand === "arbeitet" || stand.zustand === "pause";

  const punkt = arbeitet
    ? "bg-green-600"
    : krank
      ? "bg-red-600"
      : urlaub
        ? "bg-blue-600"
        : stand.zustand === "vergessen"
          ? "bg-marke"
          : "border border-linie/60";

  const text = arbeitet
    ? "text-green-800"
    : krank
      ? "text-red-800"
      : urlaub
        ? "text-blue-800"
        : stand.zustand === "vergessen"
          ? "text-verweis"
          : "text-leise";

  const uhrzeit = stand.seit
    ? new Date(stand.seit).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })
    : "";

  const wort =
    stand.zustand === "arbeitet"
      ? t("team.zustand.arbeitet", { zeit: uhrzeit })
      : stand.zustand === "pause"
        ? t("team.zustand.pause")
        : stand.zustand === "vergessen"
          ? t("team.zustand.vergessen")
          : stand.zustand === "abwesend"
            ? t(`antrag.art.${stand.grund}`)
            : t("team.zustand.nichtDa");

  return (
    <span className={`flex shrink-0 items-center gap-1.5 text-xs font-semibold ${text}`}>
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${punkt}`} aria-hidden="true" />
      {wort}
    </span>
  );
}

function EinladungDialog({ onClose, onFertig }: { onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [rolle, setRolle] = useState<string>("employee");
  const [sprache, setSprache] = useState("de");
  // Vollzeit ist der Regelfall im Betrieb. Wer weniger arbeitet, aendert die
  // Zahl; wer nichts aendert, bekommt trotzdem ein Soll.
  const [stunden, setStunden] = useState("40");
  const [eintritt, setEintritt] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      const res = await api.post<{ token?: string; verschickt: boolean }>("/auth/invitations", {
        email,
        displayName: name,
        role: rolle,
        locale: sprache,
        wochenstunden: stunden === "" ? null : Number(stunden),
        eintritt: eintritt || null
      });
      /*
       * Geht keine Post raus, kommt der Link in der Antwort zurueck. Sonst
       * liesse sich kein Zugang einrichten, bevor der Mailversand steht.
       */
      if (!res.verschickt && res.token) setToken(res.token);
      else onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  if (token) {
    const link = `${window.location.origin}/einladung/${token}`;
    return (
      <Dialog titel={t("team.einladen")} onClose={onFertig}>
        <p className="text-sm text-leise">{t("team.keinVersand")}</p>
        <p className="mt-2 break-all rounded bg-grund p-3 font-mono text-sm">{link}</p>
        <div className="mt-4">
          <Knopf onClick={onFertig}>{t("aktion.fertig")}</Knopf>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog titel={t("team.einladen")} onClose={onClose}>
      <Fehler text={fehler} />
      <p className="mb-4 text-sm text-leise">{t("team.einladenHinweis")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("team.name")}>
          <Eingabe value={name} onChange={(e) => setName(e.target.value)} />
        </Feld>
        <Feld label={t("team.email")}>
          <Eingabe type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Feld>
        <Feld label={t("team.rolleLabel")}>
          <Auswahl value={rolle} onChange={(e) => setRolle(e.target.value)}>
            {ROLLEN.map((r) => <option key={r} value={r}>{t(`team.rolle.${r}`)}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("kunden.sprache")}>
          <Auswahl value={sprache} onChange={(e) => setSprache(e.target.value)}>
            {SPRACHEN.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("team.wochenstunden")} hinweis={t("team.wochenstundenHinweis")}>
          <Eingabe
            type="number" min={0} max={80} step={0.5}
            value={stunden} onChange={(e) => setStunden(e.target.value)}
          />
        </Feld>
        <Feld label={t("team.eintritt")} hinweis={t("team.eintrittHinweis")}>
          <Eingabe type="date" value={eintritt} onChange={(e) => setEintritt(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || !email || !name} onClick={() => void speichern()}>
          {t("team.einladungSenden")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function PersonDialog({
  person, abteilungen, fahrer, onClose, onFertig
}: {
  person: Person; abteilungen: Abteilung[]; fahrer: Person[];
  onClose: () => void; onFertig: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(person.display_name);
  const [email, setEmail] = useState(person.email);
  const [rolle, setRolle] = useState(person.role);
  const [sprache, setSprache] = useState(person.locale);
  const [telefon, setTelefon] = useState(person.phone ?? "");
  const [stunden, setStunden] = useState(person.weekly_hours ?? "");
  const [eintritt, setEintritt] = useState(person.employment_start?.slice(0, 10) ?? "");
  const [faehrtMit, setFaehrtMit] = useState(person.faehrtMitId ?? "");
  const [wohnort, setWohnort] = useState(person.wohnort ?? "");
  const [planPosition, setPlanPosition] = useState(
    person.planPosition === null ? "" : String(person.planPosition)
  );
  const [imDienstplan, setImDienstplan] = useState(person.imDienstplan);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [austritt, setAustritt] = useState(false);
  const [bilanz, setBilanz] = useState<Austrittsbilanz | null>(null);
  /* Was aus dem Zurücksetzen des Zugangs wurde: verschickt, oder der Link. */
  const [zugang, setZugang] = useState<{ an: string; verschickt: boolean; token?: string } | null>(null);
  /*
   * Was dieser Mensch macht, und ob er dort nur aushilft. Beides in einer
   * Liste: getrennt gepflegt liefe es nach dem dritten Klick auseinander.
   */
  const [gewaehlt, setGewaehlt] = useState<{ abteilungId: string; aushilfe: boolean }[]>(
    person.abteilungen.map((a) => ({ abteilungId: a.id, aushilfe: a.aushilfe }))
  );

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.patch(`/auth/users/${person.id}`, {
        displayName: name,
        // Nur schicken, wenn sie sich geaendert hat: sonst prueft der Server
        // bei jedem Speichern eine Adresse gegen sich selbst.
        ...(email.trim().toLowerCase() !== person.email.toLowerCase()
          ? { email: email.trim() } : {}),
        role: rolle,
        locale: sprache,
        phone: telefon || null,
        wochenstunden: stunden === "" ? null : Number(stunden),
        eintritt: eintritt || null,
        faehrtMitId: faehrtMit || null,
        wohnort: wohnort.trim() || null,
        planPosition: planPosition === "" ? null : Number(planPosition),
        imDienstplan
      });
      /*
       * Die Abteilungen werden immer vollstaendig gesetzt, nicht einzeln
       * hinzugefuegt und entfernt. Nach einem verlorenen Aufruf stuende
       * sonst jemand in einer Abteilung, aus der er heraus ist.
       */
      await api.put(`/auth/users/${person.id}/abteilungen`, { abteilungen: gewaehlt });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={person.display_name} onClose={onClose}>
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("team.name")}>
          <Eingabe value={name} onChange={(e) => setName(e.target.value)} />
        </Feld>
        <Feld label={t("team.rolleLabel")}>
          <Auswahl value={rolle} onChange={(e) => setRolle(e.target.value)}>
            {ROLLEN.map((r) => <option key={r} value={r}>{t(`team.rolle.${r}`)}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("team.wochenstunden")} hinweis={t("team.wochenstundenHinweis")}>
          <Eingabe
            type="number" min={0} max={80} step={0.5}
            value={stunden} onChange={(e) => setStunden(e.target.value)}
          />
        </Feld>
        <Feld label={t("team.eintritt")} hinweis={t("team.eintrittHinweis")}>
          <Eingabe type="date" value={eintritt} onChange={(e) => setEintritt(e.target.value)} />
        </Feld>
        {/*
          * Die Anmeldeadresse. Aenderbar, weil sie verlorengeht: ein
          * gesperrtes Telefon, ein gekuendigtes Postfach. Ein zweites Konto
          * waere die falsche Antwort darauf, denn dann stuenden Arbeitszeiten
          * und Zugang auf verschiedenen Konten.
          */}
        <Feld label={t("team.email")} hinweis={t("team.emailHinweis")} breit>
          <Eingabe type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Feld>
        <Feld label={t("kunden.telefon")}>
          <Eingabe value={telefon} onChange={(e) => setTelefon(e.target.value)} />
        </Feld>
        <Feld label={t("kunden.sprache")}>
          <Auswahl value={sprache} onChange={(e) => setSprache(e.target.value)}>
            {SPRACHEN.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </Auswahl>
        </Feld>
        {/*
          * Wer kein eigenes Auto hat, faehrt mit. Zur Auswahl stehen nur
          * Menschen, die selbst fahren: sonst entstuende eine Kette, an deren
          * Ende niemand mehr weiss, wessen Wagen vor dem Haus steht.
          */}
        <Feld label={t("team.faehrtMit")} hinweis={t("team.faehrtMitHinweis")} breit>
          <Auswahl value={faehrtMit} onChange={(e) => setFaehrtMit(e.target.value)}>
            <option value="">{t("team.faehrtSelbst")}</option>
            {fahrer.map((f) => (
              <option key={f.id} value={f.id}>{f.display_name}</option>
            ))}
          </Auswahl>
        </Feld>
        {/*
          * Der Wohnort, und ausdruecklich keine Anschrift. Gebraucht wird nur
          * die Gegend, damit die Route am richtigen Ende beginnt; eine
          * Hausnummer waere eine Personendatenerhebung ohne Zweck.
          */}
        <Feld label={t("team.wohnort")} hinweis={t("team.wohnortHinweis")} breit>
          <Eingabe
            type="text" maxLength={120} value={wohnort}
            onChange={(e) => setWohnort(e.target.value)}
          />
          {person.wohnort && !person.wohnortGefunden && (
            <p className="mt-1 text-sm text-red-700">{t("team.wohnortOhneTreffer")}</p>
          )}
        </Feld>
        {/*
          * Wo die Spalte auf dem Dienstplan steht, und ob sie ueberhaupt
          * steht. Reine Anzeige: die Zahl bedeutet keinen Rang, und das
          * Ausblenden sperrt niemanden. Wer ausgeblendet ist und doch einen
          * Termin bekommt, erscheint an dem Tag trotzdem.
          */}
        <Feld label={t("team.planPosition")} hinweis={t("team.planPositionHinweis")}>
          <Eingabe
            type="number" min={1} max={99} value={planPosition}
            onChange={(e) => setPlanPosition(e.target.value)}
          />
        </Feld>
        <Feld label={t("team.imDienstplan")} hinweis={t("team.imDienstplanHinweis")}>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={imDienstplan}
              onChange={(e) => setImDienstplan(e.target.checked)}
            />
            {t("team.imDienstplanAn")}
          </label>
        </Feld>
      </div>

      {/*
        * Was dieser Mensch kann. Bewusst keine Sperre im Dienstplan: wer
        * ausnahmsweise im Garten aushilft, soll das duerfen. Aber am
        * Wochenbrett faellt es dann auf.
        */}
      {abteilungen.length > 0 && (
        <div className="mt-4">
          <Feld label={t("team.abteilungen")} hinweis={t("team.abteilungenHinweis")}>
            <div className="flex flex-wrap gap-2">
              {abteilungen.map((a) => {
                const drin = gewaehlt.some((g) => g.abteilungId === a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setGewaehlt((alt) =>
                      drin
                        ? alt.filter((x) => x.abteilungId !== a.id)
                        : [...alt, { abteilungId: a.id, aushilfe: false }])}
                    className={`min-h-11 rounded border px-3 text-sm font-semibold transition ${
                      drin
                        ? "border-marke bg-marke text-white"
                        : "border-linie/40 bg-white text-tinte hover:bg-grund"
                    }`}
                  >
                    {a.name}
                  </button>
                );
              })}
            </div>

            {/*
              * Das Kennzeichen erscheint erst, wenn die Abteilung gewaehlt ist.
              * Ein Haken an einer Abteilung, die dieser Mensch gar nicht macht,
              * waere eine Angabe ohne Gegenstand.
              */}
            {gewaehlt.length > 0 && (
              <ul className="mt-3 space-y-1">
                {gewaehlt.map((g) => {
                  const name = abteilungen.find((a) => a.id === g.abteilungId)?.name;
                  if (!name) return null;
                  return (
                    <li key={g.abteilungId}>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="size-5 accent-marke"
                          checked={g.aushilfe}
                          onChange={(e) => setGewaehlt((alt) => alt.map((x) =>
                            x.abteilungId === g.abteilungId
                              ? { ...x, aushilfe: e.target.checked }
                              : x))}
                        />
                        {t("team.nurAushilfe", { abteilung: name })}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </Feld>
        </div>
      )}

      {/* Der Urlaubsanspruch haengt an Mensch und Jahr, nicht an den
          Stammdaten: er aendert sich jedes Jahr und wird jedes Jahr neu
          eingetragen. */}
      <Urlaubsanspruch person={person} onFehler={setFehler} />

      <div className="mt-6 border-t border-linie pt-4">
        <p className="text-sm text-leise">{t("team.auskunftHinweis")}</p>
        <div className="mt-3">
          <Knopf
            art="neben"
            onClick={() => void dateiLaden(
              `/auskunft/${person.id}`,
              `Auskunft-${person.display_name}.json`
            ).catch((e) => setFehler(fehlertext(e, t("fehler.laden"))))}
          >
            {t("team.auskunft")}
          </Knopf>
        </div>
      </div>

      {/*
        Der Zugang. Getrennt von den Stammdaten, weil er kein Feld ist,
        sondern ein Vorgang: es geht eine Mail raus, und der Mitarbeiter
        vergibt danach selbst ein neues Passwort. Ein neues Konto entsteht
        dabei nicht, alles bleibt an diesem Menschen haengen.
      */}
      {person.is_active && (
        <div className="mt-6 border-t border-linie pt-4">
          <p className="text-sm text-leise">{t("team.zugangHinweis")}</p>
          <div className="mt-3">
            <Knopf
              art="neben"
              disabled={laeuft}
              onClick={() => {
                setLaeuft(true);
                setFehler(null);
                void api.post<{ an: string; verschickt: boolean; token?: string }>(
                  `/auth/users/${person.id}/zugang`
                )
                  .then((a) => setZugang(a))
                  .catch((e) => setFehler(fehlertext(e, t("fehler.speichern"))))
                  .finally(() => setLaeuft(false));
              }}
            >
              {t("team.zugangNeu")}
            </Knopf>
          </div>
          {zugang && (
            <p className="mt-2 rounded bg-grund px-3 py-2 text-sm">
              {zugang.verschickt
                ? t("team.zugangGesendet", { an: zugang.an })
                : t("team.zugangOhneMail")}
              {/*
                Ohne Mailserver steht der Link hier. Er ist ein Passwort auf
                Zeit, deshalb nur einmal sichtbar und nirgends gespeichert.
              */}
              {zugang.token && (
                <span className="mt-1 block break-all font-mono text-xs">
                  {`${location.origin}/einladung/${zugang.token}`}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Der Austritt steht bewusst abgesetzt und nicht als Haekchen zwischen
          den Stammdaten. Er ist ein Vorgang mit vier Wirkungen und kein Feld,
          das man beim Speichern nebenbei umlegt. */}
      <div className="mt-6 border-t border-linie pt-4">
        {person.is_active ? (
          <>
            <p className="text-sm text-leise">{t("team.austrittHinweis")}</p>
            <div className="mt-3">
              <Knopf art="warnung" onClick={() => setAustritt(true)}>
                {t("team.austritt")}
              </Knopf>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold text-red-700">{t("team.stillgelegt")}</p>
            <div className="mt-3">
              <Knopf
                art="neben"
                disabled={laeuft}
                onClick={() => {
                  setLaeuft(true);
                  void api.post(`/auth/users/${person.id}/wiedereintritt`)
                    .then(onFertig)
                    .catch((e) => { setFehler(fehlertext(e, t("fehler.speichern"))); setLaeuft(false); });
                }}
              >
                {t("team.wiedereintritt")}
              </Knopf>
            </div>
          </>
        )}
      </div>

      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft} onClick={() => void speichern()}>{t("aktion.speichern")}</Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>

      {austritt && (
        <AustrittDialog
          person={person}
          onClose={() => setAustritt(false)}
          onFertig={(b) => { setAustritt(false); setBilanz(b); }}
        />
      )}
      {bilanz && (
        <BilanzDialog bilanz={bilanz} name={person.display_name} onClose={onFertig} />
      )}
    </Dialog>
  );
}

interface Austrittsbilanz {
  geraeteWiderrufen: number;
  diensteEntfernt: number;
  auftraegeFreigegeben: number;
}

/**
 * Der Austritt, mit Datum und Grund.
 *
 * Das Datum ist Pflicht, weil daran die Sollrechnung haengt: ab dem Austritt
 * schuldet niemand mehr Stunden. Der Grund ist freiwillig und landet nur im
 * Protokoll, nicht in einer Personalakte.
 */
function AustrittDialog({
  person, onClose, onFertig
}: { person: Person; onClose: () => void; onFertig: (b: Austrittsbilanz) => void }) {
  const { t } = useTranslation();
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function ausfuehren() {
    setLaeuft(true);
    setFehler(null);
    try {
      onFertig(await api.post<Austrittsbilanz>(`/auth/users/${person.id}/austritt`, {
        datum, grund: grund || null
      }));
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={`${t("team.austritt")}: ${person.display_name}`} onClose={onClose}>
      <Fehler text={fehler} />
      <p className="mb-4 text-sm text-leise">{t("team.austrittWarnung")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("team.austrittDatum")}>
          <Eingabe type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </Feld>
        <Feld label={t("team.austrittGrund")} hinweis={t("team.austrittGrundHinweis")}>
          <Eingabe value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf art="warnung" disabled={laeuft || !datum} onClick={() => void ausfuehren()}>
          {t("team.austrittBestaetigen")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

/**
 * Was der Austritt bewirkt hat.
 *
 * Die Zahlen stehen hier, weil sonst niemand merkt, dass drei geplante Dienste
 * verschwunden sind und ein Auftrag wieder unzugewiesen in der Liste liegt.
 * Beides muss das Buero wissen, und zwar jetzt und nicht am Montag.
 */
function BilanzDialog({
  bilanz, name, onClose
}: { bilanz: Austrittsbilanz; name: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog titel={t("team.austrittFertig", { name })} onClose={onClose}>
      <ul className="space-y-1 text-sm">
        <li>{t("team.bilanzGeraete", { count: bilanz.geraeteWiderrufen })}</li>
        <li>{t("team.bilanzDienste", { count: bilanz.diensteEntfernt })}</li>
        <li>{t("team.bilanzAuftraege", { count: bilanz.auftraegeFreigegeben })}</li>
        <li className="text-leise">{t("team.bilanzZeiten")}</li>
      </ul>
      <div className="mt-5">
        <Knopf onClick={onClose}>{t("aktion.fertig")}</Knopf>
      </div>
    </Dialog>
  );
}

/**
 * Die Abteilungen.
 *
 * Garten, Pool, Reinigung, Technik. Frei pflegbar, weil niemand von aussen
 * weiss, wie diese Firma in fuenf Jahren aufgeteilt ist.
 *
 * Geloescht wird eine Abteilung nie, sie wird stillgelegt: an ihr haengen
 * Termine und Sollstunden der Vergangenheit, und ein Bericht, in dem
 * nachtraeglich die Abteilung fehlt, ist ein Bericht mit einer Luecke.
 */
function Abteilungsliste({
  abteilungen, onGeaendert
}: { abteilungen: AbteilungVoll[]; onGeaendert: () => void }) {
  const { t, i18n } = useTranslation();
  const { bereiche } = useBereiche();
  const [name, setName] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  async function tun(fn: () => Promise<unknown>) {
    setFehler(null);
    try {
      await fn();
      onGeaendert();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <>
      <h2 className="mt-10 text-lg font-bold">{t("team.abteilungen")}</h2>
      <p className="mt-1 text-sm text-leise">{t("team.abteilungenListeHinweis")}</p>
      <Fehler text={fehler} />

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Feld label={t("team.abteilungName")}>
          <Eingabe value={name} onChange={(e) => setName(e.target.value)} />
        </Feld>
        <div className="pb-1">
          <Knopf
            disabled={name.trim().length < 2}
            onClick={() => void tun(async () => {
              await api.post("/abteilungen", { name: name.trim() });
              setName("");
            })}
          >
            {t("team.abteilungHinzu")}
          </Knopf>
        </div>
      </div>

      <ul className="mt-3 space-y-2">
        {abteilungen.map((a) => (
          <li key={a.id}>
            <Karte className="flex flex-wrap items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="font-semibold">
                  {a.name}
                  {!a.aktiv && (
                    <span className="ms-2 rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                      {t("team.stillgelegt")}
                    </span>
                  )}
                </p>
                <p className="text-xs text-leise">
                  {t("team.abteilungBelegt", { menschen: a.mitarbeiter, objekte: a.objekte })}
                </p>
              </div>
              {/*
                * Welchen Teil der Checkliste diese Abteilung abdeckt.
                *
                * Danach bekommt der Gaertner im Bericht die Gartenpunkte und
                * der Poolmann die Poolpunkte. Leer ist eine gueltige Antwort
                * und heisst "sieht alles": lieber ein Punkt zu viel als einer,
                * der still verschwindet.
                */}
              <Feld label={t("team.abteilungBereich")} hinweis={t("team.abteilungBereichHinweis")}>
                <div className="flex flex-wrap gap-3">
                  {(bereiche ?? []).filter((b) => b.is_active).map((b) => {
                    const gesetzt = (a.checklistBereiche ?? []).includes(b.key);
                    return (
                      <label key={b.key} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={gesetzt}
                          onChange={() => void tun(() => api.patch(`/abteilungen/${a.id}`, {
                            checklistBereiche: gesetzt
                              ? (a.checklistBereiche ?? []).filter((x) => x !== b.key)
                              : [...(a.checklistBereiche ?? []), b.key]
                          }))}
                        />
                        {bereichsName(bereiche, b.key, i18n.language)}
                      </label>
                    );
                  })}
                  {bereiche?.length === 0 && (
                    <span className="text-sm text-leise">{t("team.keineBereiche")}</span>
                  )}
                </div>
              </Feld>
              <Knopf
                art="neben"
                onClick={() => void tun(() => api.patch(`/abteilungen/${a.id}`, { aktiv: !a.aktiv }))}
              >
                {a.aktiv ? t("team.stilllegen") : t("team.wiederAufnehmen")}
              </Knopf>
            </Karte>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Die Feiertagsliste.
 *
 * Eine gepflegte Liste statt einer Berechnung, weil auf Mallorca drei Ebenen
 * nebeneinander gelten: national, balearisch und die Fiesta des Ortes. Jede
 * Bibliothek, die das ausrechnet, liegt bei der dritten Ebene daneben.
 */
function Feiertage() {
  const { t } = useTranslation();
  const [jahr, setJahr] = useState(() => new Date().getFullYear());
  const [liste, setListe] = useState<{ datum: string; name: string }[] | null>(null);
  const [datum, setDatum] = useState("");
  const [name, setName] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    const a = await api.get<{ feiertage: { datum: string; name: string }[] }>(
      `/feiertage?jahr=${jahr}`
    );
    setListe(a.feiertage);
  }, [jahr]);

  useEffect(() => {
    void laden().catch(() => setListe([]));
  }, [laden]);

  async function tun(fn: () => Promise<unknown>) {
    setFehler(null);
    try {
      await fn();
      await laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <>
      <h2 className="mt-10 text-lg font-bold">{t("team.feiertage")}</h2>
      <p className="mt-1 text-sm text-leise">{t("team.feiertageHinweis")}</p>
      <Fehler text={fehler} />

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Feld label={t("team.jahr")}>
          <Eingabe
            type="number" min={2020} max={2099} value={jahr}
            onChange={(e) => setJahr(Number(e.target.value) || jahr)}
          />
        </Feld>
        <Feld label={t("antrag.datum")}>
          <Eingabe type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </Feld>
        <Feld label={t("team.feiertagName")}>
          <Eingabe value={name} onChange={(e) => setName(e.target.value)} />
        </Feld>
        <div className="pb-1">
          <Knopf
            disabled={!datum || !name}
            onClick={() => void tun(async () => {
              await api.post("/feiertage", { datum, name });
              setDatum("");
              setName("");
            })}
          >
            {t("team.feiertagHinzu")}
          </Knopf>
        </div>
      </div>

      {liste && liste.length > 0 && (
        <ul className="mt-3 space-y-1">
          {liste.map((f) => (
            <li key={f.datum} className="flex items-baseline gap-3 rounded bg-white px-3 py-2 text-sm">
              <span className="w-28 shrink-0 font-semibold tabular-nums">{kurz(f.datum)}</span>
              <span>{f.name}</span>
              <button
                className="ms-auto text-xs text-leise underline"
                onClick={() => void tun(() => api.del(`/feiertage/${f.datum}`))}
              >
                {t("aktion.loeschen")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Dialog({
  titel, children, onClose
}: { titel: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">{titel}</h2>
        {children}
        <button className="sr-only" onClick={onClose}>schliessen</button>
      </div>
    </div>
  );
}

function kurz(tag: string): string {
  return new Date(`${tag.slice(0, 10)}T12:00:00Z`).toLocaleDateString(sprache(), {
    weekday: "short", day: "2-digit", month: "2-digit", year: "numeric"
  });
}

/**
 * Der Urlaubsanspruch je Jahr.
 *
 * Bewusst ohne Fortschreibung aus dem Vorjahr: ein Anspruch, der sich von
 * selbst weiterschreibt, steht im dritten Jahr falsch da, ohne dass jemand
 * hinsieht. Einmal im Jahr eine Zahl einzutragen ist zumutbar.
 */
function Urlaubsanspruch({
  person, onFehler
}: { person: Person; onFehler: (t: string | null) => void }) {
  const { t } = useTranslation();
  const [jahr, setJahr] = useState(() => new Date().getFullYear());
  const [konto, setKonto] = useState<{
    einheit: string; anspruch: number | null; uebertrag: number;
    genommen: number; verbleibend: number | null;
  } | null>(null);
  const [tage, setTage] = useState("");
  const [uebertrag, setUebertrag] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState(false);

  const laden = useCallback(async () => {
    const k = await api.get<{
      einheit: string; anspruch: number | null; uebertrag: number;
      genommen: number; verbleibend: number | null;
    }>(`/urlaubskonto?jahr=${jahr}&benutzerId=${person.id}`);
    setKonto(k);
    setTage(k.anspruch === null ? "" : String(k.anspruch));
    setUebertrag(String(k.uebertrag));
  }, [jahr, person.id]);

  useEffect(() => { void laden().catch(() => setKonto(null)); }, [laden]);

  async function speichern() {
    setLaeuft(true);
    setFertig(false);
    onFehler(null);
    try {
      await api.put("/urlaubskonto", {
        benutzerId: person.id,
        jahr,
        tage: Number(tage) || 0,
        uebertrag: Number(uebertrag) || 0
      });
      await laden();
      setFertig(true);
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="mt-6 border-t border-linie pt-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="font-bold">{t("urlaub.anspruchTitel")}</h3>
        {konto && (
          <span className="text-sm text-leise">{t(`urlaub.einheit.${konto.einheit}`)}</span>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Feld label={t("team.jahr")}>
          <Eingabe
            type="number" min={2000} max={2100} value={jahr}
            onChange={(e) => setJahr(Number(e.target.value) || jahr)}
          />
        </Feld>
        <Feld label={t("urlaub.anspruch")} hinweis={t("urlaub.anspruchHinweis")}>
          <Eingabe
            type="number" min={0} max={400} step={0.5}
            value={tage} onChange={(e) => setTage(e.target.value)}
          />
        </Feld>
        <Feld label={t("urlaub.uebertrag")} hinweis={t("urlaub.uebertragHinweis")}>
          <Eingabe
            type="number" min={0} max={400} step={0.5}
            value={uebertrag} onChange={(e) => setUebertrag(e.target.value)}
          />
        </Feld>
      </div>

      {konto && konto.anspruch !== null && (
        <p className="mt-2 text-sm text-leise">
          {t("urlaub.standChef", { genommen: konto.genommen, rest: konto.verbleibend ?? 0 })}
        </p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <Knopf art="neben" disabled={laeuft || tage === ""} onClick={() => void speichern()}>
          {t("urlaub.anspruchSpeichern")}
        </Knopf>
        {fertig && <span className="text-sm text-leise">{t("einstellungen.gespeichert")}</span>}
      </div>
    </div>
  );
}
